import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const StoryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  line-height: 1.8;
`;

const StoryHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  padding: 3rem 2rem;
  border-radius: 16px;
  margin: 0 0 3rem 0;
  
  @media (max-width: 767px) {
    margin: 0 -2rem 3rem -2rem;
    border-radius: 0;
  }
`;

const StoryTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const StorySubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  font-style: italic;
`;

const Section = styled(motion.section)`
  margin-bottom: 3rem;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const OurStory = () => {
  const journalContent = `# Maggie's Miracle Story - Personal Journal Entry

**Written by Scott Soward**  
*Sunday, June 22, 2025, 5:14 PM MDT*

---

Today, as I sit down to reflect on the incredible journey we've been through, I feel a deep sense of gratitude. It's been nearly seven months since that fateful Thanksgiving Day, November 28, 2024, when Maggie's life hung by a thread, and now, many months later, I want to share her miracle with the world. This site is our way of pouring out the details—hour by hour, day by day—preserving every moment of her survival, our family's resilience, and the outpouring of faith and love from our community.This also represents the 7 months of trying to fully understand everything that happened that day.

## The Beginning: Thanksgiving Morning

It all began early that Thursday morning, between 7:00 and 9:00 AM. Isaac and I were up Israel Canyon, practicing with his .22 rifle, and when I returned, I began preparing for our Goblin Valley campout that weekend (planned for November 29–December 1) with the boys—Isaac, Noah, Declan, and Ian. I was in the kitchen, researching Dutch oven soup recipes, and calling into Maggie who was resting on her bed in the master bedroom. Maggie decided to get up and walk into the pantry. We later learned that her Fitbit showed an elevated heart rate at that time. In the pantry, she knelt down and picked out three large potatoes for our meal. 

Then, at 12:20 PM, everything changed. She handed me the third potato, said, "I feel like I'm going to pass out," and collapsed onto my left shoe. Her fingers and toes twisted in a seizure, her face turned gray, eyes were shut. Panic shot through me as I nudged her, called her name, and told her to breathe.

## The Emergency Response

At 12:21 PM, I tried CPR and mouth-to-mouth, realizing there was no breath, no heartbeat. I told Isaac to get Eliza, who'd just recertified for the 3rd time in  CPR. Ike told me he was going to call 911. Noah shouted, "Mom needs CPR!" as Eliza rushed down in a towel. By 12:22 PM, she took over, checking for a pulse—none—and began 30 chest compressions for nearly five minutes. Isaac handed me the phone, the dispatcher counting "1, 2, 3, 4" as Eliza counted to 30. Maggie moaned, a sound that was horrifying. Noah gave Eliza his shirt to cover her, and the dispatcher instructed us to unlock the door and secure the pets. 

At 12:23 PM, Noah, with tears in his eyes, started texting hundreds of contacts: "Please pray for my mom, we think she just had a heart attack." His prayer mobilization stretched across states, a network of faith I'll never forget. 

By 12:25 PM, nine first responders—EMTs, police, firefighters—arrived within five minutes, led by a female firefighter. Isaac guided them to Maggie, and they took over after Eliza's heroic effort. From 12:26 to 12:50 PM, they slid Maggie to the kitchen, moved the table, and delivered three defibrillation shocks. One of the officers escorted Eliza and I to the front room as an officer updated us. Her moans, captured by the refrigerator camera every few seconds, echoed in my mind. After 26 minutes, they detected a weak pulse, loaded her onto a gurney, and gave her an IV through the femoral artery and rushed her to the ER. 

At 12:30 PM, Isabel and Declan arrived distraught between 12:50 and 1:00 PM, Lala and I held each other while we listened to an EMT who described for us what had just taken place and what was going to happen. Downstairs, Noah got the girls to pray. Anna stepped up, watching and caring for her sisters with such tenderness during the chaos. 

Between 12:50 and 1:00 PM, Heather Jordan and Kirsten Hall and Tiffany Bryant came in, went downstairs to the girls and cared for the kids, while twenty-five neighbors stood outside. Eliza drove us to Lehi Mountain Point Hospital.

## Hospital Journey

From 1:00 to 2:00 PM, I called Ben to let him know the status, and Bishop Clint Nelson met us at the hospital. The ER nurse confirmed the ventilator and a pulse. Bishop and I gave Maggie a blessing. I anointed her, and Bishop blessed her, while Chaplain Dave offered comfort. Maggie, with a breathing tube and IVs, looked lifeless. I blessed Noah too. That evening, she moved to the ICU under Dr. Ghali and Dr. Al Jandali's care, and I feared brain damage. 

## Recovery Week: Day by Day

### November 29 (Friday)

At 4:00 PM, doctors said she was doing really well. They fed her through a tube, weaned her off drugs to reduce sedation—she couldn't breathe on her own sedated—and planned to remove the tube the next day. She communicated with her eyebrows, squeezed our hands, and wiggled her toes. I wrote, "I think she's gonna be OK." Ingrid, her sister, had the first meaningful conversation with her, recording Maggie's near-death experience on "Ingrids interview of Maggie's NDE.m4a." Visitors—Ron, Marlese, Lee, Brit, Bethany—filled the room with love.

### November 30 (Saturday)

Her short-term memory started returning. Doctors got her moving, possibly on solid food, and evaluated her throat, but no discharge. She walked the ICU floor, used the bathroom alone, and had visits all day, though propofol left her loopy with spotty memory. They found part of her heart wasn't functioning, causing arrhythmia, and scheduled a cardiologist for Monday. I noted, "Overall, terrifying experience, but there were multiple miracles along the way."

### December 1 (Sunday)

She was still loopy, her short-term memory not fully back but better. The cardiologist confirmed the apex of her heart wasn't working, which caused atrial fibrillation (AFib) two additional times while in the hospital, and we discussed options. She was transferred by ambulance to St. Mark's Hospital ICU. Meanwhile, Violet, with her gentle spirit, gave a beautiful testimony that Sunday in church, thanking our amazing neighbors while Maggie was in the hospital, a moment that still moves me. Eliza also bore her testimony and spoke of miracles that occurred to save her mom's life.

### December 2 (Monday)

At 2:13 PM, we arrived at room 5N22. She was doing great, most memory back, but they said her arm would be in a sling for weeks post-ICD surgery, with the date unclear until evaluation. We had a family blessing.

### December 3 (Tuesday)

Another echocardiogram showed the akinetic region was now small, though prior records were missing for comparison. An MRI at 4:00 PM showed her heart, and they planned ICD surgery for December 4.

### December 4 (Wednesday)

Surgery started at 6:10 PM, finished at 8:20 PM, a success by Dr. Nannapaneni, with Ben Lambert's blessing. Ingrid talked with her.

### December 5 (Thursday)

Discharge papers started. We expected to be home by 4:00 PM. Maggie was cleared and discharged.

## Reflections on Faith and Miracles

As I write this, I'm struck by the convergence of so many miraculous elements: Eliza's recent CPR training, Isaac being home to call 911, Noah's immediate prayer mobilization, the swift response of first responders on a holiday, and the countless prayers from our community. Each detail seems orchestrated by a loving God who knew what our family would need.

The medical team called it a 5% survival rate for what Maggie experienced. But we know it was more than medicine—it was faith, family, community, and divine intervention working together to bring our wife and mother back to us.

Maggie's experience on the other side of the veil, her conversations with heavenly messengers who told her "It isn't your time," and her overwhelming feeling of God's love and mercy have strengthened our entire family's faith. Her primary prayer had always been that she could raise her children, and that prayer was answered in the most miraculous way.

Today, as we share this story with the world, we do so with hearts full of gratitude—for Maggie's life, for our children's courage, for our community's love, and for a God who hears and answers prayers.

This miracle belongs not just to our family, but to everyone who prayed, fasted, served, and believed. Thank you for being part of Maggie's miracle.

---

*Written with love and gratitude,*  
**Scott Soward**`;

  return (
    <StoryContainer>
      <SEO 
        title="Our Complete Story"
        description="Read Scott Soward's detailed account of Maggie's miraculous survival after cardiac arrest. From Thanksgiving morning preparations to 26 minutes without a pulse to complete recovery - a family's journey through crisis, faith, and healing."
        url="/story"
        image="/uploads/hospital-journey/IMG_1008.jpeg"
        type="article"
      />
      <StoryHeader>
        <StoryTitle>Our Story</StoryTitle>
        <StorySubtitle>
          Written by Scott Soward, Sunday, June 22, 2025, 5:14 PM MDT
        </StorySubtitle>
      </StoryHeader>

      <Section
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div style={{
          background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
          padding: '2rem',
          borderRadius: '12px',
          borderLeft: '4px solid #2c5282'
        }}>
          <div style={{
            flex: 1,
            lineHeight: 1.8,
            fontSize: '1.1rem',
            color: '#2c3e50'
          }}>
            {journalContent.split('\n\n').map((paragraph, index) => {
              if (paragraph.startsWith('# ')) {
                return <h1 key={index} style={{ color: '#27ae60', fontSize: '2rem', marginBottom: '10px' }}>{paragraph.replace('# ', '')}</h1>;
              } else if (paragraph.startsWith('## ')) {
                return <h2 key={index} style={{ color: '#2c3e50', fontSize: '1.5rem', marginTop: '30px', marginBottom: '15px' }}>{paragraph.replace('## ', '')}</h2>;
              } else if (paragraph.startsWith('### ')) {
                return <h3 key={index} style={{ color: '#4a6741', fontSize: '1.3rem', marginTop: '25px', marginBottom: '10px' }}>{paragraph.replace('### ', '')}</h3>;
              } else if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                return <p key={index} style={{ fontWeight: 600, color: '#27ae60', marginBottom: '15px' }}>{paragraph.replace(/\*\*/g, '')}</p>;
              } else if (paragraph.startsWith('*') && paragraph.endsWith('*')) {
                return <p key={index} style={{ fontStyle: 'italic', opacity: 0.8, marginBottom: '15px' }}>{paragraph.replace(/\*/g, '')}</p>;
              } else if (paragraph.startsWith('---')) {
                return <hr key={index} style={{ margin: '30px 0', border: 'none', borderTop: '2px solid #e2e8f0' }} />;
              } else {
                return <p key={index} style={{ marginBottom: '20px', textAlign: 'left' }}>{paragraph}</p>;
              }
            })}
          </div>
        </div>
      </Section>
    </StoryContainer>
  );
};

export default OurStory;
